import { db } from '../firebaseConfig';
import { doc, updateDoc, getDoc, setDoc, collection, query, where, getDocs } from 'firebase/firestore';
import logger from '../utils/logger';

/**
 * Service zum Verwalten von Angeboten
 */
export const offerService = {
  /**
   * Markiert ein Angebot als 'in Rechnung gestellt'
   * 
   * @param offerId Die ID des Angebots, das markiert werden soll
   * @param invoiceId Optional: Die ID der erstellten Rechnung
   * @param invoiceNumber Optional: Die Rechnungsnummer
   * @returns Promise<boolean> True, wenn das Update erfolgreich war
   */
  async markOfferAsInvoiced(offerId: string, invoiceId?: string, invoiceNumber?: string): Promise<boolean> {
    try {
      if (!offerId) {
        logger.error('Keine Angebots-ID angegeben');
        return false;
      }

      const offerRef = doc(db, 'offers', offerId);
      
      // Aktualisiere das Angebot mit mehreren Status-Flags um kompatibel zu bleiben
      await updateDoc(offerRef, {
        // Status-Felder, die in verschiedenen Teilen der Anwendung geprüft werden könnten
        invoiced: true,
        hasInvoice: true,
        workflowStatus: 'invoiced', 
        status: 'invoiced',  // Überschreibe den aktuellen Status
        
        // Zusätzliche Informationen zur Nachverfolgung
        invoiceId: invoiceId || null,
        invoiceNumber: invoiceNumber || null,
        invoicedAt: new Date().toISOString(),
      });
      
      logger.info(`Angebot ${offerId} erfolgreich als 'in Rechnung gestellt' markiert`);
      return true;
    } catch (error) {
      logger.error('Fehler beim Markieren des Angebots als in Rechnung gestellt:', error);
      return false;
    }
  },

  /**
   * Überprüft, ob ein Angebot bereits in Rechnung gestellt wurde
   * 
   * @param offerId Die ID des zu prüfenden Angebots
   * @returns Promise<boolean> True, wenn das Angebot bereits in Rechnung gestellt wurde
   */
  async isOfferInvoiced(offerId: string): Promise<boolean> {
    try {
      if (!offerId) {
        return false;
      }

      const offerRef = doc(db, 'offers', offerId);
      const offerSnapshot = await getDoc(offerRef);
      
      if (!offerSnapshot.exists()) {
        return false;
      }
      
      const offerData = offerSnapshot.data();
      
      // Prüfe alle möglichen Status-Flags
      return !!(
        offerData.invoiced === true || 
        offerData.hasInvoice === true || 
        offerData.workflowStatus === 'invoiced' || 
        offerData.status === 'invoiced'
      );
    } catch (error) {
      logger.error('Fehler beim Prüfen des Angebotsstatus:', error);
      return false;
    }
  }
}; 